import type { EventImpact } from '@/types/event'

export type EventCountry = 'all' | 'US' | 'EU' | 'GB'
export type EventSourceFilter = 'all' | 'macro' | 'earnings'
export type EventImpactFilter = 'all' | EventImpact

interface EventsFilterBarProps {
  impact: EventImpactFilter
  country: EventCountry
  source: EventSourceFilter
  fromDate: string
  toDate: string
  isRefreshing: boolean
  onImpactChange: (v: EventImpactFilter) => void
  onCountryChange: (v: EventCountry) => void
  onSourceChange: (v: EventSourceFilter) => void
  onFromChange: (v: string) => void
  onToChange: (v: string) => void
  onRefresh: () => void
}

const SELECT_CLASS =
  'border border-hairline bg-surface px-3 py-2 text-sm text-ink focus:border-bernstein focus:outline-none'

/**
 * Filter-Bar für die /events-Page: Impact, Country, Source, Date-Range,
 * Refresh-Button. State lebt im Parent (EventsPage).
 */
export function EventsFilterBar({
  impact,
  country,
  source,
  fromDate,
  toDate,
  isRefreshing,
  onImpactChange,
  onCountryChange,
  onSourceChange,
  onFromChange,
  onToChange,
  onRefresh,
}: EventsFilterBarProps) {
  return (
    <section
      aria-label="Event-Filter"
      className="hairline-b flex flex-wrap items-center gap-4 bg-surface px-4 py-4"
    >
      <label className="flex items-center gap-2 text-xs text-muted">
        <span className="tracked-caps text-[10px] text-faint">Impact</span>
        <select
          aria-label="Impact-Filter"
          value={impact}
          onChange={(e) => onImpactChange(e.target.value as EventImpactFilter)}
          className={SELECT_CLASS}
        >
          <option value="all">Alle</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
      </label>

      <label className="flex items-center gap-2 text-xs text-muted">
        <span className="tracked-caps text-[10px] text-faint">Country</span>
        <select
          aria-label="Country-Filter"
          value={country}
          onChange={(e) => onCountryChange(e.target.value as EventCountry)}
          className={SELECT_CLASS}
        >
          <option value="all">Alle</option>
          <option value="US">US</option>
          <option value="EU">EU</option>
          <option value="GB">GB</option>
        </select>
      </label>

      <label className="flex items-center gap-2 text-xs text-muted">
        <span className="tracked-caps text-[10px] text-faint">Quelle</span>
        <select
          aria-label="Source-Filter"
          value={source}
          onChange={(e) => onSourceChange(e.target.value as EventSourceFilter)}
          className={SELECT_CLASS}
        >
          <option value="all">Alle</option>
          <option value="macro">Macro</option>
          <option value="earnings">Earnings</option>
        </select>
      </label>

      <label className="flex items-center gap-2 text-xs text-muted">
        <span className="tracked-caps text-[10px] text-faint">Von</span>
        <input
          type="date"
          aria-label="Von-Datum"
          value={fromDate}
          onChange={(e) => onFromChange(e.target.value)}
          className={SELECT_CLASS}
        />
      </label>

      <label className="flex items-center gap-2 text-xs text-muted">
        <span className="tracked-caps text-[10px] text-faint">Bis</span>
        <input
          type="date"
          aria-label="Bis-Datum"
          value={toDate}
          onChange={(e) => onToChange(e.target.value)}
          className={SELECT_CLASS}
        />
      </label>

      <button
        type="button"
        onClick={onRefresh}
        disabled={isRefreshing}
        className="tracked-caps w-full border border-bernstein px-4 py-2 text-[11px] text-bernstein transition-colors hover:bg-bernstein hover:text-surface disabled:opacity-50 md:ml-auto md:w-auto"
      >
        {isRefreshing ? 'Aktualisiere…' : '⟳ Aktualisieren'}
      </button>
    </section>
  )
}
